import { useEffect, useState, useSyncExternalStore } from 'react'
import { api } from '../api'
import type { Client } from '../../shared/types'
import {
  addClientBinding,
  describeKey,
  getBindings,
  isChord,
  keyChips,
  removeBinding,
  resetBindings,
  RESERVED,
  setKeys,
  subscribeBindings,
  syncClientLabels,
} from '../shortcuts'

function useBindings() {
  return useSyncExternalStore(subscribeBindings, getBindings, getBindings)
}

function Keys({ keys }: { keys: string }) {
  if (!keys) return <span className="text-xs text-slate-300">unassigned</span>
  return (
    <span className="flex items-center gap-1">
      {keys.split(' ').map((step, si) => (
        <span key={si} className="flex items-center gap-0.5">
          {si > 0 && <span className="mr-0.5 text-[10px] text-slate-300">then</span>}
          {keyChips(step).map((chip, i) => (
            <kbd
              key={i}
              className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-xs text-slate-600"
            >
              {chip}
            </kbd>
          ))}
        </span>
      ))}
    </span>
  )
}

/**
 * Captures either one modifier chord (Ctrl+Shift+T) or a two-key sequence.
 *
 * While capturing, every keydown is swallowed — otherwise the global handler
 * would navigate away mid-capture, which is exactly the sort of thing that
 * makes a rebinding UI feel broken.
 */
function useKeyCapture(active: boolean, onDone: (keys: string) => void, onCancel: () => void) {
  const [first, setFirst] = useState<string | null>(null)

  useEffect(() => {
    if (!active) {
      setFirst(null)
      return
    }
    const onKey = (e: KeyboardEvent) => {
      // Capture everything, including combinations the browser would act on,
      // so a rebind can't trigger the thing it's rebinding.
      e.preventDefault()
      e.stopPropagation()

      if (e.key === 'Escape' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        onCancel()
        return
      }
      const step = describeKey(e)
      if (!step) return // still holding modifiers down

      // Ctrl/Alt/Meta combinations stand alone; plain keys pair up.
      if (first == null) {
        if (isChord(step)) onDone(step)
        else setFirst(step)
        return
      }
      onDone(`${first} ${step}`)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [active, first, onDone, onCancel])

  return first
}

export default function ShortcutSettings({ onClose }: { onClose: () => void }) {
  const bindings = useBindings()
  const [clients, setClients] = useState<Client[]>([])
  const [editing, setEditing] = useState<string | null>(null)
  const [adding, setAdding] = useState<number | null>(null)

  useEffect(() => {
    api
      .listClients(false)
      .then((cs) => {
        setClients(cs)
        syncClientLabels(cs) // drop bindings for clients that no longer exist
      })
      .catch(() => {})
  }, [])

  const first = useKeyCapture(
    editing != null || adding != null,
    (keys) => {
      if (adding != null) {
        const c = clients.find((x) => x.id === adding)
        if (c) addClientBinding(c.id, c.simple_name, keys)
        setAdding(null)
      } else if (editing != null) {
        setKeys(editing, keys)
        setEditing(null)
      }
    },
    () => {
      setEditing(null)
      setAdding(null)
    },
  )

  const capturing = editing != null || adding != null
  const unassignedClients = clients.filter((c) => !bindings.some((b) => b.id === `client:${c.id}`))

  return (
    <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-md flex-col rounded-lg border border-slate-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="font-semibold text-slate-900">Keyboard shortcuts</h2>
          <button className="text-slate-400 hover:text-slate-700" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {capturing && (
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700">
            {first == null ? (
              <>
                Press a combination like <kbd className="font-mono">Ctrl</kbd>+<kbd className="font-mono">Shift</kbd>+
                <kbd className="font-mono">T</kbd>, or two plain keys in a row.
              </>
            ) : (
              <>
                <Keys keys={first} /> — now the second key…
              </>
            )}
            <span className="ml-2 text-xs text-slate-400">Esc to cancel</span>
          </div>
        )}

        <ul className="flex-1 divide-y divide-slate-100 overflow-y-auto">
          {bindings.map((b) => (
            <li key={b.id} className="flex items-center justify-between gap-2 px-4 py-1.5 text-sm">
              <span className="min-w-0 truncate text-slate-700">
                {b.label}
                {b.id.startsWith('client:') && <span className="ml-1 text-xs text-slate-400">client</span>}
                {RESERVED[b.keys] && (
                  <span className="ml-1 text-xs text-amber-600" title={`Overrides ${RESERVED[b.keys]}`}>
                    ⚠
                  </span>
                )}
              </span>
              <span className="flex items-center gap-2">
                <button
                  className={
                    'rounded px-1 py-0.5 ' + (editing === b.id ? 'ring-1 ring-[var(--accent)]' : 'hover:bg-slate-100')
                  }
                  title="Click, then press a combination or two plain keys"
                  onClick={() => {
                    setAdding(null)
                    setEditing(b.id)
                  }}
                >
                  <Keys keys={b.keys} />
                </button>
                {b.id.startsWith('client:') && (
                  <button
                    className="text-slate-300 hover:text-red-500"
                    title="Remove shortcut"
                    onClick={() => removeBinding(b.id)}
                  >
                    ✕
                  </button>
                )}
              </span>
            </li>
          ))}

          <li className="flex items-center justify-between gap-2 px-4 py-1.5 text-sm">
            <span className="text-slate-700">Assistant</span>
            <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-xs text-slate-600">
              Ctrl K
            </kbd>
          </li>
          <li className="flex items-center justify-between gap-2 px-4 py-1.5 text-sm">
            <span className="text-slate-700">This window</span>
            <kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-xs text-slate-600">
              ?
            </kbd>
          </li>
        </ul>

        <div className="border-t border-slate-200 px-4 py-2">
          {unassignedClients.length > 0 ? (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              Add client
              <select
                className="flex-1 rounded border border-slate-300 bg-white px-1.5 py-1 text-xs"
                value=""
                onChange={(e) => {
                  if (!e.target.value) return
                  setEditing(null)
                  setAdding(Number(e.target.value))
                }}
              >
                <option value="">Choose a client…</option>
                {unassignedClients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.simple_name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="text-xs text-slate-400">Every client has a shortcut.</p>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2">
          <p className="max-w-[70%] text-xs text-slate-400">
            Ctrl/Alt combinations work anywhere. Plain two-key sequences pause while you're typing.
          </p>
          <button className="text-xs text-slate-500 hover:text-slate-800 hover:underline" onClick={resetBindings}>
            Reset
          </button>
        </div>
      </div>
    </div>
  )
}
